import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import { FormattedMessage, useIntl } from 'react-intl';

import type { OrderDetailsDocuments } from '#api-bridge/types/orderDetailsDocuments.ts';
import { BulkMaterialDetails } from '#components/shared/BulkMaterialDetails.tsx';
import type { Material } from '#domain/Material.ts';
import { isServiceOrder } from '#domain/Order.ts';
import { parseServiceOrderStopName } from '#domain/serviceOrderStopName.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';
import type { ExpanderListItemBase } from '#utils/util.ts';

import { CargoMaterialDetails } from '../../shared/CargoMaterialDetails.tsx';
import { OrderDocuments } from './DocumentExpanderListItem.tsx';
import { OrderStopFormToExpanderListItem } from './OrderStopFormExpanderListItem.tsx';
import { OrderTourDetails } from './OrderTourDetails.tsx';

const getStopListOfOrder = (order: OrderWithSortableKey) => {
    if (isServiceOrder(order)) {
        return [
            {
                ...order.executionStop,
                name: parseServiceOrderStopName(order.executionStop.name).company,
                comment: order.note,
            },
        ];
    }
    return [
        { ...order.loadingStop, comment: order.loadingComment },
        { ...order.unloadingStop, comment: order.unloadingComment },
    ];
};

type OrderExpanderItemsProps = {
    order: OrderWithSortableKey;
    materials?: Material[];
    documents?: OrderDetailsDocuments;
    selectedItemIndex: number | undefined;
    setSelectedItemIndex: (index: number | undefined) => void;
};

export const getOrderExpanderItems = ({
    order,
    materials,
    documents,
    setSelectedItemIndex,
    selectedItemIndex,
}: OrderExpanderItemsProps) => {
    const intl = useIntl();
    const expanderItems: ExpanderListItemBase[] = [];

    const orderItems = getStopListOfOrder(order).map(order => OrderStopFormToExpanderListItem(order));
    expanderItems.push(...orderItems);

    if (materials) {
        const materialsCount = materials.length;
        const hasMaterials = materialsCount > 0;
        const materialsItem: ExpanderListItemBase = {
            id: 'materials',
            header: (
                <div data-testid={'material-title'}>
                    <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.material.title'} />
                    {hasMaterials ? ` (${materialsCount})` : null}
                </div>
            ),
            body: (
                <div className='display-flex flex-column gap-10'>
                    {hasMaterials ? (
                        materials.map(material => (
                            <div key={`${material.type}-${material.id}`}>
                                {material.type === 'cargo-material' ? (
                                    <CargoMaterialDetails material={material} />
                                ) : (
                                    <BulkMaterialDetails material={material} />
                                )}
                            </div>
                        ))
                    ) : (
                        <EmptyState
                            condensed
                            outerClassName='bg-lightest border-none'
                            headline={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.material.empty-state.headline',

                                defaultMessage: 'No cargo',
                            })}
                            message={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.material.empty-state.message',

                                defaultMessage: 'This order does not contain any cargo.',
                            })}
                        />
                    )}
                </div>
            ),
        };
        expanderItems.push(materialsItem);
    }

    const documentCount = documents?.length ?? 0;
    const documentsItem: ExpanderListItemBase = {
        id: 'documents',
        header: (
            <FormattedMessage
                data-testid={'documents-div'}
                id={
                    documentCount > 0
                        ? 'intl-msg:ordercom-cloud-frontend.order.documents.count'
                        : 'intl-msg:ordercom-cloud-frontend.order.documents.plural'
                }
                values={{ count: documentCount }}
            />
        ),
        body: <OrderDocuments documents={documents} order={order} />,
    };
    expanderItems.push(documentsItem);

    if (order.tourId) {
        const tourItem: ExpanderListItemBase = {
            id: 'tour',
            header: (
                <div data-testid='order-tour-details'>
                    <FormattedMessage id='intl-msg:ordercom-cloud-frontend.orders.details.tour.title' />
                </div>
            ),
            body: <OrderTourDetails order={order} />,
        };
        expanderItems.push(tourItem);
    }

    return expanderItems.map((item, index) => ({
        ...item,
        open: selectedItemIndex === index,
        onOpen: () => setSelectedItemIndex(index),
        onClose: () => setSelectedItemIndex(undefined),
        bodyClassName: 'padding-top-0',
    }));
};
